import { ref, onMounted, onUnmounted } from 'vue';

// Tailwind CSS lg breakpoint
const LG_BREAKPOINT = 1024;

/**
 * Composable for managing sidebar state with responsive behavior
 * - Sidebar is closed by default on mobile (< 1024px)
 * - Sidebar is open by default on desktop (>= 1024px)
 * - Changes in screen size update the default state
 */
export function useSidebarState() {
  // The admin portal is an SPA (ssr: false), so the viewport is known at setup.
  // Starting from the real value avoids the sidebar sliding in after first paint.
  const sidebarOpen = ref(typeof window !== 'undefined' && window.innerWidth >= LG_BREAKPOINT);
  // Transitions stay off until mounted so the initial state never animates.
  const transitionsReady = ref(false);
  
  // Check if we're on desktop
  const checkScreenSize = () => {
    if (typeof window !== 'undefined') {
      const isDesktop = window.innerWidth >= LG_BREAKPOINT;
      // Only auto-change if user hasn't manually toggled
      // For initial load, set based on screen size
      if (isDesktop) {
        sidebarOpen.value = true;
      } else {
        sidebarOpen.value = false;
      }
    }
  };

  // Toggle sidebar manually
  const toggleSidebar = () => {
    sidebarOpen.value = !sidebarOpen.value;
  };

  // Open sidebar
  const openSidebar = () => {
    sidebarOpen.value = true;
  };

  // Close sidebar
  const closeSidebar = () => {
    sidebarOpen.value = false;
  };

  // Handle resize
  let resizeTimeout: ReturnType<typeof setTimeout>;
  const handleResize = () => {
    clearTimeout(resizeTimeout);
    resizeTimeout = setTimeout(() => {
      const isDesktop = window.innerWidth >= LG_BREAKPOINT;
      if (isDesktop && !sidebarOpen.value) {
        // Auto-open on desktop if closed
        sidebarOpen.value = true;
      } else if (!isDesktop && sidebarOpen.value) {
        // Close on mobile
        sidebarOpen.value = false;
      }
    }, 100);
  };

  onMounted(() => {
    checkScreenSize();
    window.addEventListener('resize', handleResize);
    requestAnimationFrame(() => {
      transitionsReady.value = true;
    });
  });

  onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
    clearTimeout(resizeTimeout);
  });

  return {
    sidebarOpen,
    transitionsReady,
    toggleSidebar,
    openSidebar,
    closeSidebar,
  };
}
